import { ref, watch } from 'vue';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuthStore } from '@/stores/authStore';

export function useBarcodeGenerator() {
  const authStore = useAuthStore();

  /** Raw textarea text — one SKU per line */
  const skusText = ref('');

  /**
   * Parsed rows derived from skusText.
   * Each row: { seq: number, sku: string, barcode: string|null }
   */
  const rows = ref([]);

  const isGenerating = ref(false);
  const error = ref(null);
  let pendingNextLastSequence = null;

  // Keep rows in sync with the textarea as the user types/pastes
  watch(skusText, (text) => {
    const skus = parseSkus(text);
    const existing = new Map(rows.value.map(r => [r.sku, r]));
    rows.value = skus.map((sku, i) => {
      const prev = existing.get(sku);
      return prev
        ? { ...prev, seq: i + 1 }
        : { seq: i + 1, sku, barcode: null };
    });
  });

  function parseSkus(text) {
    return text
      .split(/[\n,]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  async function generate() {
    if (rows.value.length === 0) {
      error.value = 'Paste at least one SKU before generating.';
      return;
    }
    error.value = null;
    isGenerating.value = true;

    try {
      const token = authStore.getActiveToken();
      const { data } = await axios.post('/api/Barcode/generate-upca-batch', {
        count: rows.value.length,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      data.barcodes.forEach((barcode, i) => {
        rows.value[i].barcode = barcode;
      });
      pendingNextLastSequence = data.nextLastSequence;
    } catch {
      error.value = 'Failed to generate barcodes. Please try again.';
    } finally {
      isGenerating.value = false;
    }
  }

  /**
   * Export to Excel with the Barcode column formatted as text
   * so leading zeros are preserved and no scientific notation.
   */
  async function exportToExcel() {
    if (rows.value.length === 0) return;

    if (pendingNextLastSequence !== null) {
      try {
        const token = authStore.getActiveToken();
        await axios.post('/api/Barcode/confirm-upca-sequence', {
          nextLastSequence: pendingNextLastSequence,
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        pendingNextLastSequence = null;
      } catch {
        error.value = 'Failed to save sequence. Please try again.';
        return;
      }
    }

    const wb = XLSX.utils.book_new();

    // Build array of row objects
    const data = rows.value.map(row => ({
      '#': row.seq,
      'SKU': row.sku,
      'Barcode': row.barcode ?? '',
    }));

    const ws = XLSX.utils.json_to_sheet(data);

    // Force the Barcode column (column C) to text format so leading zeros are kept
    const barcodeCol = 'C';
    const rowCount = rows.value.length;
    for (let r = 2; r <= rowCount + 1; r++) {
      const cellRef = `${barcodeCol}${r}`;
      if (ws[cellRef]) {
        ws[cellRef].t = 's'; // string type
        ws[cellRef].z = '@'; // text format
      }
    }

    // Set column widths
    ws['!cols'] = [
      { wch: 6 },   // #
      { wch: 30 },  // SKU
      { wch: 16 },  // Barcode
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Barcodes');
    XLSX.writeFile(wb, `barcodes-${Date.now()}.xlsx`);
  }

  function reset() {
    skusText.value = '';
    rows.value = [];
    error.value = null;
    pendingNextLastSequence = null;
  }

  return {
    skusText,
    rows,
    isGenerating,
    error,
    generate,
    exportToExcel,
    reset,
  };
}

export default useBarcodeGenerator;
